import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import FormControlLabel from '@mui/material/FormControlLabel';
import MenuItem from '@mui/material/MenuItem';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import CheckCircleOutlineOutlinedIcon from '@mui/icons-material/CheckCircleOutlineOutlined';
import BlockOutlinedIcon from '@mui/icons-material/BlockOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import EventBusyOutlinedIcon from '@mui/icons-material/EventBusyOutlined';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import { DataTable, DataTableColumn, DateField, EmptyState, PageHeader, StatCard, StatusChip } from '@routes-plan/ui';
import { useResourcePermissions } from '../../../hooks/useResourcePermissions';
import { nowOperationalDateString } from '../../../utils/operationalDateTime';
import LocationPicker, { isLocationComplete, toApiLocation } from '../shared/LocationPicker';
import {
  BatchRow,
  INVENTORY_ROUTES,
  OfficeOption,
  Product,
  STOCK_STATUS_LABEL,
  ShowAlerts,
  StockRow,
  StockStatus,
  apiErrorMessage,
  fetchOffices,
  fetchStock,
  formatDate,
  formatMoney,
  locationParams,
} from '../shared/inventoryApi';
import { FilterGrid, IntField, PageBody, PageTabs, useInventoryAlert } from '../shared/ui';
import { TextKey, useT } from '../../../i18n';
import { setPageTitle } from '../../../utils/pageTitle';
import { ScreenTourButton } from '../../../components/help/ScreenHelp';

type Loc = { officeId: number | null; routeId: number | null };

/** Clave de texto de cada tipo de movimiento del kardex, por su `code`. */
const MOVEMENT_TYPES: Record<string, TextKey> = {
  INGRESO: 'inventory.movement.INGRESO',
  BAJA: 'inventory.movement.BAJA',
  PRESTAMO: 'inventory.movement.PRESTAMO',
  DEVOLUCION_PRESTAMO: 'inventory.movement.DEVOLUCION_PRESTAMO',
  TRASLADO_SALIDA: 'inventory.movement.TRASLADO_SALIDA',
  TRASLADO_ENTRADA: 'inventory.movement.TRASLADO_ENTRADA',
  AJUSTE_ENTRADA: 'inventory.movement.AJUSTE_ENTRADA',
  AJUSTE_SALIDA: 'inventory.movement.AJUSTE_SALIDA',
  ANULACION_INGRESO: 'inventory.movement.ANULACION_INGRESO',
  DEVOLUCION_CLIENTE: 'inventory.movement.DEVOLUCION_CLIENTE',
  ENTREGA_REPOSICION: 'inventory.movement.ENTREGA_REPOSICION',
  CAMBIO_ESTADO_SALIDA: 'inventory.movement.CAMBIO_ESTADO_SALIDA',
  CAMBIO_ESTADO_ENTRADA: 'inventory.movement.CAMBIO_ESTADO_ENTRADA',
};

/** Consultas de inventario: existencias por ubicación, kardex y lotes por vencer. */
const Stock: React.FC<ShowAlerts> = () => {
  const t = useT();
  const [tab, setTab] = useState(0);
  setPageTitle('inventory.stock.pageTitle');
  return (
    <PageBody>
      <PageHeader title={t('inventory.stock.title')} description={t('inventory.stock.description')} helpAction={<ScreenTourButton />} />
      <PageTabs value={tab} onChange={setTab} labels={[t('inventory.stock.tabStock'), t('inventory.stock.tabKardex'), t('inventory.stock.tabExpiring')]} tour="stock-tabs" />
      {tab === 0 && <StockTab />}
      {tab === 1 && <KardexTab />}
      {tab === 2 && <ExpiringTab />}
    </PageBody>
  );
};

const useOffices = () => {
  const [offices, setOffices] = useState<OfficeOption[]>([]);
  useEffect(() => {
    fetchOffices().then(setOffices).catch(() => setOffices([]));
  }, []);
  return offices;
};

const statusText = (s: string) => STOCK_STATUS_LABEL[s as StockStatus] ?? s;
const statusChip = (s: string) => <StatusChip label={statusText(s)} tone={s === 'DISPONIBLE' ? 'success' : 'warning'} />;

const StockTab: React.FC = () => {
  const t = useT();
  const showAlert = useInventoryAlert();
  const { canRead } = useResourcePermissions(INVENTORY_ROUTES.stock);
  const offices = useOffices();
  const [location, setLocation] = useState<Loc>({ officeId: null, routeId: null });
  const [rows, setRows] = useState<StockRow[]>([]);
  const [batches, setBatches] = useState<BatchRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [showBatches, setShowBatches] = useState(false);

  const search = useCallback(async () => {
    if (!canRead || !isLocationComplete(location)) return;
    setLoading(true);
    try {
      const { data, batches: b } = await fetchStock(toApiLocation(location), true);
      setRows(data);
      setBatches(b);
    } catch (error) {
      showAlert(apiErrorMessage(error, t('inventory.stock.loadError')), 'error');
    } finally {
      setLoading(false);
    }
  }, [canRead, location, showAlert, t]);

  useEffect(() => {
    search();
  }, [search]);

  const below = (r: StockRow) => r.minStock != null && Number(r.available) < Number(r.minStock);
  const totals = useMemo(
    () => ({
      available: rows.reduce((a, r) => a + Number(r.available || 0), 0),
      notLendable: rows.reduce((a, r) => a + Number(r.notLendable || 0), 0),
      below: rows.filter(below).length,
    }),
    [rows],
  );

  const columns: DataTableColumn<StockRow>[] = [
    { id: 'title', header: t('inventory.common.product'), value: (r) => r.title, sortable: true },
    { id: 'sku', header: t('inventory.stock.sku'), value: (r) => r.sku ?? '', hideBelow: 'sm' },
    { id: 'available', header: t('inventory.status.available'), align: 'right', sortable: true, value: (r) => Number(r.available) },
    { id: 'notLendable', header: t('inventory.status.notLendable'), align: 'right', sortable: true, value: (r) => Number(r.notLendable) },
    { id: 'minStock', header: t('inventory.stock.minStock'), align: 'right', value: (r) => r.minStock ?? null, hideBelow: 'md' },
    {
      id: 'alert',
      header: t('inventory.stock.alert'),
      value: (r) => (below(r) ? t('inventory.stock.belowMin') : ''),
      render: (r) => (below(r) ? <StatusChip label={t('inventory.stock.belowMin')} tone="warning" /> : null),
    },
    { id: 'price', header: t('inventory.stock.price'), align: 'right', value: (r) => Number(r.price), render: (r) => formatMoney(r.price), hideBelow: 'sm' },
    {
      id: 'enabled',
      header: t('common.enabled'),
      value: (r) => (r.enabled ? t('common.yes') : t('common.no')),
      render: (r) => <StatusChip label={r.enabled ? t('common.yes') : t('common.no')} tone={r.enabled ? 'success' : 'neutral'} />,
      hideBelow: 'md',
    },
  ];

  const batchColumns: DataTableColumn<BatchRow>[] = [
    { id: 'title', header: t('inventory.common.product'), value: (b) => b.title, sortable: true },
    { id: 'lot', header: t('inventory.common.batch'), value: (b) => b.lotCode || t('inventory.common.batchFallback', { id: b.batchId }) },
    { id: 'expires', header: t('inventory.common.expires'), value: (b) => formatDate(b.expirationDate), sortable: true },
    { id: 'status', header: t('common.status'), value: (b) => statusText(b.status), render: (b) => statusChip(b.status) },
    { id: 'quantity', header: t('inventory.common.quantity'), align: 'right', value: (b) => Number(b.quantity), sortable: true },
    { id: 'cost', header: t('inventory.common.unitCostShort'), align: 'right', value: (b) => Number(b.unitCost), render: (b) => formatMoney(b.unitCost), hideBelow: 'sm' },
  ];

  const hasLocation = isLocationComplete(location);
  const empty = <EmptyState dense icon={<Inventory2OutlinedIcon fontSize="inherit" />} title={t('inventory.stock.noUnits')} />;

  return (
    <>
      <Card>
        <CardContent sx={{ display: 'grid', gap: 1 }}>
          <LocationPicker offices={offices} value={location} onChange={setLocation} tourPrefix="stock" />
          <FormControlLabel
            control={<Switch checked={showBatches} onChange={(e) => setShowBatches(e.target.checked)} />}
            label={t('inventory.stock.showByBatch')}
            data-tour="stock-batches"
            sx={{ justifySelf: 'start' }}
          />
        </CardContent>
      </Card>
      {hasLocation && (
        <Box data-tour="stock-stats" sx={{ display: 'grid', gap: 2, gridTemplateColumns: 'repeat(auto-fit, minmax(min(150px, 100%), 1fr))' }}>
          <StatCard label={t('inventory.stock.products')} value={rows.length} icon={<Inventory2OutlinedIcon />} loading={loading} />
          <StatCard label={t('inventory.stock.unitsAvailable')} value={totals.available} icon={<CheckCircleOutlineOutlinedIcon />} loading={loading} />
          <StatCard label={t('inventory.stock.unitsNotLendable')} value={totals.notLendable} icon={<BlockOutlinedIcon />} loading={loading} />
          <StatCard label={t('inventory.stock.belowMinCount')} value={totals.below} icon={<WarningAmberOutlinedIcon />} loading={loading} />
        </Box>
      )}
      <Card>
        <CardContent>
          {!hasLocation ? (
            <EmptyState icon={<PlaceOutlinedIcon fontSize="inherit" />} title={t('inventory.stock.selectLocation')} description={t('inventory.stock.selectLocationHint')} />
          ) : showBatches ? (
            <DataTable<BatchRow>
              key="batches"
              rows={batches}
              columns={batchColumns}
              getRowId={(b) => `${b.batchId}-${b.status}`}
              loading={loading}
              ariaLabel={t('inventory.stock.showByBatch')}
              tourId="stock-lots"
              searchPlaceholder={t('inventory.stock.searchProduct')}
              exportFileName={t('inventory.stock.batchesExportFile')}
              emptyState={empty}
            />
          ) : (
            <DataTable<StockRow>
              key="products"
              rows={rows}
              columns={columns}
              getRowId={(r) => r.productId}
              loading={loading}
              ariaLabel={t('inventory.stock.tabStock')}
              tourId="stock"
              searchPlaceholder={t('inventory.stock.searchProduct')}
              exportFileName={t('inventory.stock.exportFile')}
              initialSort={{ columnId: 'title', direction: 'asc' }}
              emptyState={empty}
            />
          )}
        </CardContent>
      </Card>
    </>
  );
};

type Movement = {
  id: number;
  date: string;
  movementType?: { code?: string; name?: string } | null;
  office?: { name: string } | null;
  route?: { name: string } | null;
  product?: { title?: string } | null;
  batch?: { id: number; lotCode?: string | null } | null;
  stockStatus: string;
  quantity: number;
  balanceAfter: number;
  loan?: { id: number } | null;
  observation?: string | null;
  user?: { username?: string } | null;
};

const KardexTab: React.FC = () => {
  const t = useT();
  const showAlert = useInventoryAlert();
  const offices = useOffices();
  const [location, setLocation] = useState<Loc>({ officeId: null, routeId: null });
  const [products, setProducts] = useState<Product[]>([]);
  const [productId, setProductId] = useState<number | ''>('');
  const [type, setType] = useState('');
  const [start, setStart] = useState(`${nowOperationalDateString().slice(0, 8)}01`);
  const [end, setEnd] = useState(nowOperationalDateString());
  const [rows, setRows] = useState<Movement[]>([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  useEffect(() => {
    axios.get('products').then(({ data }) => setProducts(Array.isArray(data?.data) ? data.data : [])).catch(() => setProducts([]));
  }, []);

  const search = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get('inventory/movements', {
        params: {
          ...(isLocationComplete(location) ? locationParams(toApiLocation(location)) : {}),
          ...(productId !== '' ? { productId: productId } : {}),
          ...(type ? { type } : {}),
          startDate: start,
          endDate: end,
        },
      });
      // En orden de ocurrencia (el más antiguo arriba): el saldo se lee de arriba hacia abajo.
      setRows((Array.isArray(data?.data) ? (data.data as Movement[]) : []).sort((a, b) => Number(a.id) - Number(b.id)));
      setSearched(true);
    } catch (error) {
      showAlert(apiErrorMessage(error, t('inventory.kardex.loadError')), 'error');
    } finally {
      setLoading(false);
    }
  };

  const typeLabel = (m: Movement) => (m.movementType?.code && MOVEMENT_TYPES[m.movementType.code] ? t(MOVEMENT_TYPES[m.movementType.code]) : m.movementType?.name ?? '');
  const columns: DataTableColumn<Movement>[] = [
    { id: 'date', header: t('common.date'), value: (m) => formatDate(m.date) },
    { id: 'type', header: t('inventory.kardex.movement'), value: typeLabel },
    {
      id: 'location',
      header: t('inventory.common.location'),
      value: (m) => (m.route ? t('inventory.location.route', { name: m.route.name }) : m.office ? t('inventory.location.office', { name: m.office.name }) : ''),
      hideBelow: 'sm',
    },
    { id: 'product', header: t('inventory.common.product'), value: (m) => m.product?.title ?? '' },
    { id: 'lot', header: t('inventory.common.batch'), value: (m) => (m.batch ? m.batch.lotCode || t('inventory.common.batchFallback', { id: m.batch.id }) : ''), hideBelow: 'md' },
    { id: 'status', header: t('common.status'), value: (m) => statusText(m.stockStatus), hideBelow: 'md' },
    { id: 'entry', header: t('inventory.kardex.entry'), align: 'right', value: (m) => (Number(m.quantity) > 0 ? Number(m.quantity) : null) },
    { id: 'exit', header: t('inventory.kardex.exit'), align: 'right', value: (m) => (Number(m.quantity) < 0 ? Math.abs(Number(m.quantity)) : null) },
    { id: 'balance', header: t('inventory.kardex.balance'), align: 'right', value: (m) => Number(m.balanceAfter) },
    { id: 'ref', header: t('inventory.kardex.reference'), value: (m) => (m.loan?.id ? t('inventory.kardex.loanRef', { id: m.loan.id }) : m.observation ?? ''), wrap: true, hideBelow: 'md' },
    { id: 'user', header: t('common.user'), value: (m) => m.user?.username ?? '', hideBelow: 'md' },
  ];

  return (
    <>
      <Card>
        <CardContent sx={{ display: 'grid', gap: 2 }}>
          <LocationPicker offices={offices} value={location} onChange={setLocation} tourPrefix="kardex" />
          <FilterGrid>
            <TextField
              select
              size="small"
              fullWidth
              label={t('inventory.common.product')}
              value={productId === '' ? '' : String(productId)}
              onChange={(e) => setProductId(Number(e.target.value) || '')}
              SelectProps={{ displayEmpty: true }}
              InputLabelProps={{ shrink: true }}
            >
              <MenuItem value="">{t('common.all')}</MenuItem>
              {products.map((p) => (
                <MenuItem key={p.id} value={String(p.id)}>
                  {p.title}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              size="small"
              fullWidth
              label={t('inventory.kardex.movement')}
              value={type}
              onChange={(e) => setType(String(e.target.value))}
              SelectProps={{ displayEmpty: true }}
              InputLabelProps={{ shrink: true }}
            >
              <MenuItem value="">{t('common.all')}</MenuItem>
              {Object.entries(MOVEMENT_TYPES).map(([code, labelKey]) => (
                <MenuItem key={code} value={code}>
                  {t(labelKey)}
                </MenuItem>
              ))}
            </TextField>
            <DateField size="small" fullWidth label={t('inventory.filter.from')} value={start || null} max={end || undefined} onChange={(v) => setStart(v ?? '')} />
            <DateField size="small" fullWidth label={t('inventory.filter.to')} value={end || null} min={start || undefined} onChange={(v) => setEnd(v ?? '')} />
            <Button variant="contained" startIcon={<SearchOutlinedIcon />} onClick={search} disabled={loading} sx={{ minHeight: 40 }}>
              {t('inventory.filter.query')}
            </Button>
          </FilterGrid>
        </CardContent>
      </Card>
      <Card>
        <CardContent>
          <DataTable<Movement>
            rows={rows}
            columns={columns}
            getRowId={(m) => m.id}
            loading={loading}
            ariaLabel={t('inventory.stock.tabKardex')}
            tourId="kardex"
            exportFileName={t('inventory.kardex.exportFile')}
            emptyState={
              <EmptyState
                icon={<ReceiptLongOutlinedIcon fontSize="inherit" />}
                title={searched ? t('inventory.kardex.empty') : t('inventory.kardex.notSearched')}
                description={searched ? undefined : t('inventory.kardex.notSearchedHint')}
              />
            }
          />
        </CardContent>
      </Card>
    </>
  );
};

type ExpiringRow = {
  batchId: number;
  title: string;
  lotCode?: string | null;
  expirationDate: string;
  officeId?: number | null;
  officeName?: string | null;
  routeId?: number | null;
  routeName?: string | null;
  status: string;
  quantity: number;
  expired: boolean;
};

const ExpiringTab: React.FC = () => {
  const t = useT();
  const showAlert = useInventoryAlert();
  const offices = useOffices();
  const [officeId, setOfficeId] = useState<number | ''>('');
  const [days, setDays] = useState('30');
  const [rows, setRows] = useState<ExpiringRow[]>([]);
  const [loading, setLoading] = useState(true);

  const search = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get('product-batches/expiring', { params: { days: Number(days) || 0, ...(officeId ? { officeId: officeId } : {}) } });
      setRows(Array.isArray(data?.data) ? data.data : []);
    } catch (error) {
      showAlert(apiErrorMessage(error, t('inventory.expiring.loadError')), 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    search();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const columns: DataTableColumn<ExpiringRow>[] = [
    { id: 'title', header: t('inventory.common.product'), value: (r) => r.title, sortable: true },
    { id: 'lot', header: t('inventory.common.batch'), value: (r) => r.lotCode || t('inventory.common.batchFallback', { id: r.batchId }) },
    { id: 'expires', header: t('inventory.common.expires'), value: (r) => formatDate(r.expirationDate), sortable: true },
    {
      id: 'expired',
      header: t('inventory.expiring.state'),
      value: (r) => (r.expired ? t('inventory.expiring.expired') : t('inventory.expiring.expiring')),
      render: (r) => <StatusChip label={r.expired ? t('inventory.expiring.expired') : t('inventory.expiring.expiring')} tone={r.expired ? 'danger' : 'warning'} />,
    },
    {
      id: 'location',
      header: t('inventory.common.location'),
      value: (r) => (r.routeName ? t('inventory.location.route', { name: r.routeName }) : t('inventory.location.office', { name: r.officeName ?? '' })),
      hideBelow: 'sm',
    },
    { id: 'status', header: t('common.status'), value: (r) => statusText(r.status), render: (r) => statusChip(r.status), hideBelow: 'md' },
    { id: 'quantity', header: t('inventory.common.quantity'), align: 'right', value: (r) => Number(r.quantity), sortable: true },
  ];

  return (
    <>
      <Card>
        <CardContent>
          <FilterGrid>
            <TextField
              select
              size="small"
              fullWidth
              label={t('common.office')}
              value={officeId === '' ? '' : String(officeId)}
              onChange={(e) => setOfficeId(Number(e.target.value) || '')}
              SelectProps={{ displayEmpty: true }}
              InputLabelProps={{ shrink: true }}
            >
              <MenuItem value="">{t('inventory.filter.allOffices')}</MenuItem>
              {offices.map((o) => (
                <MenuItem key={o.id} value={String(o.id)}>
                  {o.name}
                </MenuItem>
              ))}
            </TextField>
            <IntField size="small" fullWidth label={t('inventory.expiring.days')} value={days} onChange={setDays} />
            <Button variant="contained" startIcon={<SearchOutlinedIcon />} onClick={search} disabled={loading} sx={{ minHeight: 40 }}>
              {t('inventory.filter.query')}
            </Button>
          </FilterGrid>
        </CardContent>
      </Card>
      <Card>
        <CardContent>
          <DataTable<ExpiringRow>
            rows={rows}
            columns={columns}
            getRowId={(r) => `${r.batchId}-${r.officeId ?? ''}-${r.routeId ?? ''}-${r.status}`}
            loading={loading}
            ariaLabel={t('inventory.stock.tabExpiring')}
            tourId="expiring"
            exportFileName={t('inventory.expiring.exportFile')}
            initialSort={{ columnId: 'expires', direction: 'asc' }}
            emptyState={<EmptyState icon={<EventBusyOutlinedIcon fontSize="inherit" />} title={t('inventory.expiring.empty')} />}
          />
        </CardContent>
      </Card>
    </>
  );
};

export default Stock;
